import React from 'react';
import { X, Check, ArrowRight } from 'lucide-react';

const OLD_WAY_ITEMS = [
  'Manual scheduling across spreadsheets',
  'No real-time visibility into attendance',
  'Payroll errors from untracked hours',
  'Zero oversight across multiple locations',
];

const NEW_WAY_ITEMS = [
  'Reduce labor costs with real-time budget tracking',
  'Prevent overtime with automated scheduling alerts',
  'Improve staff efficiency across every location',
  'Manage multiple locations from one dashboard',
];

export function TransformationSection() {
  return (
    <section className="py-10 md:py-24 bg-slate-50 px-4" aria-label="Transformation">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="text-center mb-8 md:mb-14">
          <h2
            className="text-[26px] md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight"
            style={{ fontFamily: 'Manrope' }}
          >
            Stop managing your team<br className="hidden sm:block" /> with outdated systems
          </h2>
        </div>

        {/* Old Way */}
        <div className="rounded-3xl bg-white border-2 border-red-200 overflow-hidden" style={{ borderTop: '4px solid #ef4444' }}>
          <div className="p-6 md:p-12">
            <div className="inline-flex items-center gap-2 bg-red-100 border border-red-300 rounded-full px-3 py-1 md:px-4 md:py-1.5 mb-5 md:mb-8">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0"></span>
              <span className="text-red-600 text-[10px] md:text-xs font-bold uppercase tracking-widest">The Old Way</span>
            </div>
            <div className="grid md:grid-cols-2 gap-6 md:gap-12 items-center">
              <div>
                <img
                  src="https://restaff.b-cdn.net/Screenshots/Old%20Way%20To%20Schedule.png"
                  alt="Chaotic manual scheduling with spreadsheets and paper before ReStaff"
                  className="rounded-2xl shadow-lg w-full border border-red-100"
                  loading="lazy"
                  width="600"
                  height="400"
                />
              </div>
              <div>
                <p className="text-slate-900 text-base md:text-xl font-bold leading-snug md:leading-relaxed mb-6 md:mb-10">
                  Manual scheduling. Scattered communication. Disconnected systems.
                </p>
                <ul className="space-y-3 md:space-y-6">
                  {OLD_WAY_ITEMS.map((item) => (
                    <li key={item} className="flex items-center gap-3 md:gap-4">
                      <span className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-red-100 border-2 border-red-300 flex items-center justify-center flex-shrink-0">
                        <X size={14} className="text-red-600 md:!w-4 md:!h-4" />
                      </span>
                      <span className="text-slate-900 text-sm md:text-lg font-semibold">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="flex flex-col items-center py-6 md:py-10 gap-2 md:gap-3">
          <div className="w-px h-6 md:h-10 bg-gradient-to-b from-rose-200 to-orange-300"></div>
          <div className="bg-orange-500 text-white text-base md:text-2xl font-bold px-6 md:px-10 py-3 md:py-5 rounded-2xl shadow-lg shadow-orange-200 tracking-wide text-center">
            Now imagine everything in one place.
          </div>
          <div className="w-px h-6 md:h-10 bg-gradient-to-b from-orange-300 to-emerald-200"></div>
        </div>

        {/* New Way */}
        <div className="rounded-3xl bg-white border-2 border-emerald-200 overflow-hidden" style={{ borderTop: '4px solid #10b981' }}>
          <div className="p-6 md:p-12">
            <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-5 md:mb-8">
              <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-300 rounded-full px-3 py-1 md:px-4 md:py-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 flex-shrink-0"></span>
                <span className="text-emerald-700 text-[10px] md:text-xs font-bold uppercase tracking-widest">The ReStaff Way</span>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <div className="w-7 h-7 md:w-8 md:h-8 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xs">RS</span>
                </div>
                <span className="font-bold text-base md:text-lg text-slate-900" style={{ fontFamily: 'Manrope' }}>ReStaff</span>
              </div>
            </div>
            <div className="flex flex-col lg:flex-row gap-6 items-center">
              <div className="order-2 lg:order-1 lg:w-[320px] flex-shrink-0">
                <p className="text-slate-900 text-base md:text-xl font-bold leading-snug md:leading-relaxed mb-6 md:mb-10">
                  One platform for employees, labor costs, and scheduling — across every location.
                </p>
                <ul className="space-y-3 md:space-y-6">
                  {NEW_WAY_ITEMS.map((item) => (
                    <li key={item} className="flex items-center gap-3 md:gap-4">
                      <span className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-green-100 border-2 border-green-300 flex items-center justify-center flex-shrink-0">
                        <Check size={14} className="text-green-700 md:!w-4 md:!h-4" strokeWidth={3} />
                      </span>
                      <span className="text-slate-900 text-sm md:text-lg font-semibold">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="hidden lg:flex lg:order-2 items-center justify-center flex-shrink-0 px-2">
                <ArrowRight size={44} className="text-red-500" strokeWidth={2.5} />
              </div>
              <div className="order-1 lg:order-3 lg:flex-1 w-full">
                <img
                  src="https://restaff.b-cdn.net/Screenshots/Schedule.png"
                  alt="Centralized workforce scheduling and management with ReStaff"
                  className="rounded-2xl shadow-2xl border border-slate-200 w-full object-cover object-top"
                  style={{ minHeight: '220px', maxHeight: '480px' }}
                  loading="lazy"
                  width="1000"
                  height="680"
                />
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
